import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SurveySelectComponent } from './SurveySelectComponent';

// Custom TipTap node for survey selection
export const SurveyNode = Node.create({
  name: 'surveySelect',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      onSurveySelect: null,
      onSurveyRemove: null,
    };
  },

  addAttributes() {
    return {
      surveyId: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-survey-select]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-survey-select': '',
        class: 'survey-node-container',
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SurveySelectComponent, {
      onSurveySelect: this.options.onSurveySelect,
      onSurveyRemove: this.options.onSurveyRemove,
    });
  },

  addCommands() {
    return {
      insertSurvey:
        (attributes) =>
        ({ commands, tr }) => {
          // Check if there's space at the cursor position
          const $from = tr.selection.$from;
          const after = $from.nodeAfter;

          // If there's content after cursor and no space, add a space or new line
          if (after && after.text && !after.text.startsWith(' ')) {
            commands.insertContent(' ');
          }

          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
      deleteSurvey:
        () =>
        ({ state, dispatch }) => {
          const { selection } = state;
          const { $anchor } = selection;

          // Try to find survey node before or after cursor
          let nodeToDelete = null;
          let pos = null;

          if (
            $anchor.nodeBefore &&
            $anchor.nodeBefore.type.name === this.name
          ) {
            nodeToDelete = $anchor.nodeBefore;
            pos = $anchor.pos - nodeToDelete.nodeSize;
          } else if (
            $anchor.nodeAfter &&
            $anchor.nodeAfter.type.name === this.name
          ) {
            nodeToDelete = $anchor.nodeAfter;
            pos = $anchor.pos;
          }

          if (nodeToDelete && pos !== null) {
            // Call onSurveyRemove if callback exists
            if (this.options.onSurveyRemove && nodeToDelete.attrs?.surveyId) {
              const surveyId = nodeToDelete.attrs.surveyId;
              this.options.onSurveyRemove({ value: surveyId, surveyId });
            }

            if (dispatch) {
              const tr = state.tr.delete(pos, pos + nodeToDelete.nodeSize);
              dispatch(tr);
            }
            return true;
          }

          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Handle backspace to delete the node
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $anchor } = selection;

        // Check if we're at the start of the node or the node is selected
        if ($anchor.nodeBefore && $anchor.nodeBefore.type.name === this.name) {
          return this.editor.commands.deleteSurvey();
        }

        return false;
      },
      // Handle delete key as well
      Delete: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $anchor } = selection;

        // Check if the node after cursor is a survey node
        if ($anchor.nodeAfter && $anchor.nodeAfter.type.name === this.name) {
          return this.editor.commands.deleteSurvey();
        }

        return false;
      },
    };
  },
});
